import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, CalendarIcon, ClockIcon, UsersIcon, MapPinIcon, StarIcon, HeartIcon, ShareIcon, CheckIcon, InfoIcon, GlobeIcon, DollarSignIcon, FileTextIcon, PlayIcon, DownloadIcon, PrinterIcon, PlusIcon, MinusIcon, ArrowRightIcon, PhoneIcon, MailIcon, SendIcon, AlertCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { usePresentationMode } from '../../contexts/PresentationModeContext';
import { useClosingAssistant } from '../../contexts/ClosingAssistantContext';
import { WeatherInsights } from '../weather/WeatherInsights';
import { QuoteFlow } from '../quotes/QuoteFlow';
export const ProductDetails = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState<any>(null);
  const {
    isPresentationMode
  } = usePresentationMode();
  const {
    recordSectionView,
    recordTimeSpent,
    updateReadinessScore,
    customerProfile
  } = useClosingAssistant();
  const [showQuoteFlow, setShowQuoteFlow] = useState(false);
  const handleOpenQuoteFlow = () => {
    setShowQuoteFlow(true);
  };
  useEffect(() => {
    // Simulate API fetch
    setLoading(true);
    setTimeout(() => {
      // Mock product data
      setProduct({
        id: parseInt(id || '1'),
        title: 'Japan Cultural Explorer',
        destination: 'Japan',
        duration: '10 days',
        price: 2899,
        image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        category: 'git',
        rating: 4.8,
        reviews: 124,
        groupSize: '8-16 people',
        featured: true,
        departures: [{
          date: '2024-07-15',
          price: 2899,
          availability: 'available',
          seats: 8,
          totalSeats: 20,
          soldSeats: 12
        }, {
          date: '2024-08-12',
          price: 3099,
          availability: 'limited',
          seats: 3,
          totalSeats: 20,
          soldSeats: 17
        }, {
          date: '2024-09-05',
          price: 2999,
          availability: 'available',
          seats: 12,
          totalSeats: 20,
          soldSeats: 8
        }, {
          date: '2024-10-10',
          price: 2799,
          availability: 'available',
          seats: 14,
          totalSeats: 20,
          soldSeats: 6
        }, {
          date: '2024-11-15',
          price: 2699,
          availability: 'soldout',
          seats: 0,
          totalSeats: 20,
          soldSeats: 20
        }],
        // Pricing tiers
        pricingTiers: [{
          threshold: 5,
          price: 2899
        }, {
          threshold: 10,
          price: 2799
        }, {
          threshold: 15,
          price: 2699
        }],
        currentTier: 0,
        description: 'Immerse yourself in the rich cultural heritage of Japan on this comprehensive 10-day tour. From the bustling streets of Tokyo to the serene temples of Kyoto, experience the perfect blend of ancient traditions and modern innovations.',
        highlights: ["Explore Tokyo's vibrant neighborhoods and historic sites", 'Experience a traditional tea ceremony in Kyoto', 'Visit the iconic Mount Fuji and surrounding lakes', 'Stay in a traditional ryokan with onsen hot springs', 'Learn the art of sushi making with a master chef', 'Discover the historic temples and shrines of Nara'],
        itinerary: [{
          day: 1,
          title: 'Arrival in Tokyo',
          description: 'Arrive at Narita International Airport and transfer to your hotel in Tokyo. After check-in, enjoy a welcome dinner with your tour group.',
          meals: ['dinner'],
          accommodation: 'Hotel Gracery Shinjuku or similar (4-star)'
        }, {
          day: 2,
          title: 'Tokyo Exploration',
          description: "Full day exploring Tokyo's highlights including Meiji Shrine, Harajuku, Shibuya Crossing, and the Tokyo Metropolitan Government Building for panoramic city views.",
          meals: ['breakfast', 'lunch'],
          accommodation: 'Hotel Gracery Shinjuku or similar (4-star)'
        }, {
          day: 3,
          title: 'Tokyo to Mt. Fuji',
          description: 'Travel to the iconic Mt. Fuji and explore the surrounding Five Lakes region. Visit Oshino Hakkai, a set of eight natural springs, and experience spectacular views from the 5th Station of Mt. Fuji (weather permitting).',
          meals: ['breakfast', 'lunch', 'dinner'],
          accommodation: 'Fuji View Hotel or similar (4-star)'
        }, {
          day: 4,
          title: 'Mt. Fuji to Kyoto',
          description: "Morning at leisure around Lake Kawaguchi. Afternoon bullet train to Kyoto. Evening walking tour of Gion, Kyoto's famous geisha district.",
          meals: ['breakfast'],
          accommodation: 'Hotel Granvia Kyoto or similar (4-star)'
        }, {
          day: 5,
          title: 'Kyoto Exploration',
          description: "Full day exploring Kyoto's UNESCO World Heritage sites including Kinkaku-ji (Golden Pavilion), Nijo Castle, and Kiyomizu-dera Temple. Afternoon tea ceremony experience.",
          meals: ['breakfast', 'lunch'],
          accommodation: 'Hotel Granvia Kyoto or similar (4-star)'
        }, {
          day: 6,
          title: 'Nara Day Trip',
          description: "Day trip to nearby Nara, Japan's first permanent capital. Visit Todai-ji Temple housing the Great Buddha, and Nara Park, home to hundreds of freely roaming deer.",
          meals: ['breakfast'],
          accommodation: 'Hotel Granvia Kyoto or similar (4-star)'
        }, {
          day: 7,
          title: 'Kyoto to Hakone',
          description: 'Morning train to Hakone, part of Fuji-Hakone-Izu National Park. Afternoon cruise on Lake Ashi with views of Mt. Fuji. Experience a traditional ryokan stay with onsen hot spring baths.',
          meals: ['breakfast', 'dinner'],
          accommodation: 'Hakone Yumoto Onsen Ryokan or similar (traditional)'
        }, {
          day: 8,
          title: 'Hakone to Tokyo',
          description: 'Morning at leisure to enjoy the ryokan facilities. Afternoon return to Tokyo. Evening sushi-making class followed by dinner.',
          meals: ['breakfast', 'dinner'],
          accommodation: 'Hotel Gracery Shinjuku or similar (4-star)'
        }, {
          day: 9,
          title: 'Tokyo Free Day',
          description: 'Full day at leisure to explore Tokyo on your own or join an optional excursion to Nikko or Kamakura (additional cost).',
          meals: ['breakfast'],
          accommodation: 'Hotel Gracery Shinjuku or similar (4-star)'
        }, {
          day: 10,
          title: 'Departure',
          description: 'Transfer to Narita International Airport for your departure flight.',
          meals: ['breakfast'],
          accommodation: null
        }],
        usefulInfo: [{
          title: 'Visa Requirements',
          content: 'Most visitors from the US, Canada, UK, EU, and Australia can enter Japan without a visa for stays up to 90 days for tourism purposes. Please check current requirements based on nationality.'
        }, {
          title: 'Weather',
          content: 'Japan has four distinct seasons. Spring (March-May) and autumn (September-November) are the most popular times to visit with mild temperatures and beautiful scenery. Summer (June-August) can be hot and humid. Winter (December-February) is cold with snow in northern regions.'
        }, {
          title: 'Packing Tips',
          content: 'Comfortable walking shoes, modest clothing for temple visits, adaptable layers for varying temperatures, small gifts from your home country for guides, portable Wi-Fi or SIM card for navigation.'
        }, {
          title: 'Currency',
          content: "The currency in Japan is the Japanese Yen (JPY). Credit cards are widely accepted in urban areas, but it's advisable to carry cash for smaller establishments and rural areas."
        }, {
          title: 'Language',
          content: 'Japanese is the official language. English is spoken at major tourist sites and hotels but less so in rural areas. A translation app or pocket dictionary can be helpful.'
        }, {
          title: 'Health & Safety',
          content: 'Japan is one of the safest countries for travelers. No specific vaccinations are required. Tap water is safe to drink. Medical facilities are excellent, but can be expensive, so travel insurance is recommended.'
        }],
        documents: [{
          title: 'Japan Explorer Brochure',
          type: 'pdf',
          size: '2.4 MB',
          url: '#'
        }, {
          title: 'Detailed Itinerary',
          type: 'pdf',
          size: '1.8 MB',
          url: '#'
        }, {
          title: 'Booking Terms & Conditions',
          type: 'pdf',
          size: '520 KB',
          url: '#'
        }, {
          title: 'Travel Insurance Options',
          type: 'pdf',
          size: '780 KB',
          url: '#'
        }],
        videos: [{
          title: 'Japan Explorer Highlights',
          thumbnail: 'https://images.unsplash.com/photo-1528164344705-47542687000d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
          duration: '3:45',
          url: '#'
        }, {
          title: 'Tokyo City Guide',
          thumbnail: 'https://images.unsplash.com/photo-1503899036084-c55cdd92da26?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
          duration: '5:20',
          url: '#'
        }, {
          title: 'Customer Testimonials',
          thumbnail: 'https://images.unsplash.com/photo-1522623349500-de37a56ea2a5?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
          duration: '2:15',
          url: '#'
        }]
      });
      setLoading(false);
    }, 500);
  }, [id]);
  useEffect(() => {
    if (!isPresentationMode) {
      recordSectionView(activeTab);
      // Update readiness score based on tab
      if (activeTab === 'price') {
        updateReadinessScore(Math.min(100, customerProfile.readinessScore + 15));
      } else if (activeTab === 'itinerary') {
        updateReadinessScore(Math.min(100, customerProfile.readinessScore + 5));
      }
      // Start timer for time spent tracking
      const startTime = Date.now();
      return () => {
        const timeSpent = Math.round((Date.now() - startTime) / 1000);
        recordTimeSpent(activeTab, timeSpent);
      };
    }
  }, [activeTab, isPresentationMode]);
  if (loading) {
    return <div className="flex items-center justify-center h-full" data-id="element-2962">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" data-id="element-2963"></div>
      </div>;
  }
  if (!product) {
    return <div className="flex flex-col items-center justify-center h-full" data-id="element-2964">
        <p className="text-xl text-gray-600" data-id="element-2965">Product not found</p>
        <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md" onClick={() => navigate('/products')} data-id="element-2966">
          Back to Products
        </button>
      </div>;
  }
  // Get the next departure with available seats
  const nextAvailableDeparture = product.departures.find((dep: any) => dep.availability !== 'soldout');
  // Calculate the next pricing tier
  const getNextPricingTier = () => {
    if (product.currentTier < product.pricingTiers.length - 1) {
      return product.pricingTiers[product.currentTier + 1];
    }
    return null;
  };
  const nextTier = getNextPricingTier();
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <div className="space-y-6" data-id="element-2967">
            <p className="text-gray-700" data-id="element-2968">{product.description}</p>
            {/* Weather Insights component */}
            <WeatherInsights destination={product.destination} data-id="element-2969" />
            {/* Availability and Pricing Section - hidden or simplified in presentation mode */}
            {!isPresentationMode ? <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 space-y-6 agent-only" data-id="element-2970">
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center" data-id="element-2971">
                  <CalendarIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-2972" />
                  Current Availability & Pricing
                </h3>
                {nextAvailableDeparture ? <>
                    {/* Next Departure Info */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between bg-white p-4 rounded-lg border border-blue-100" data-id="element-2973">
                      <div data-id="element-2974">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-2975">
                          Next Departure
                        </div>
                        <div className="font-medium text-gray-800" data-id="element-2976">
                          {new Date(nextAvailableDeparture.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                        </div>
                      </div>
                      <div className="mt-3 md:mt-0" data-id="element-2977">
                        <div className="flex items-center mb-1" data-id="element-2978">
                          <UsersIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-2979" />
                          <span className="text-sm text-gray-600" data-id="element-2980">
                            {nextAvailableDeparture.soldSeats} seats sold |{' '}
                            {nextAvailableDeparture.seats} available
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-2981">
                          <div className={`h-2.5 rounded-full ${nextAvailableDeparture.seats <= 3 ? 'bg-red-500' : nextAvailableDeparture.seats <= 6 ? 'bg-amber-500' : 'bg-green-500'}`} style={{
                      width: `${nextAvailableDeparture.soldSeats / nextAvailableDeparture.totalSeats * 100}%`
                    }} data-id="element-2982"></div>
                        </div>
                      </div>
                      {nextAvailableDeparture.seats <= 3 && <div className="flex items-center mt-3 md:mt-0 bg-red-50 text-red-700 px-3 py-1 rounded-md text-sm" data-id="element-2983">
                          <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-2984" />
                          Only {nextAvailableDeparture.seats} seats left!
                        </div>}
                    </div>
                    {/* Pricing Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2985">
                      <div className="bg-white p-4 rounded-lg border border-blue-100" data-id="element-2986">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-2987">
                          Current Price (per person)
                        </div>
                        <div className="text-2xl font-bold text-blue-600" data-id="element-2988">
                          ${nextAvailableDeparture.price}
                        </div>
                        {nextTier && <div className="mt-2 text-sm" data-id="element-2989">
                            <div className="flex items-center text-gray-600" data-id="element-2990">
                              <ArrowRightIcon className="h-3 w-3 mr-1" data-id="element-2991" />
                              Next{' '}
                              {nextTier.threshold - product.pricingTiers[product.currentTier].threshold}{' '}
                              bookings: ${nextTier.price} per person
                            </div>
                            {nextAvailableDeparture.seats <= 5 && <div className="flex items-center text-amber-600 mt-1" data-id="element-2992">
                                <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-2993" />
                                Price will increase after{' '}
                                {nextAvailableDeparture.seats} more bookings
                              </div>}
                          </div>}
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-100" data-id="element-2994">
                        <div className="text-sm text-gray-500 mb-2" data-id="element-2995">
                          Pricing Tiers
                        </div>
                        <table className="w-full text-sm" data-id="element-2996">
                          <thead data-id="element-2997">
                            <tr className="border-b border-gray-200" data-id="element-2998">
                              <th className="text-left py-1 text-gray-600 font-medium" data-id="element-2999">
                                Bookings
                              </th>
                              <th className="text-right py-1 text-gray-600 font-medium" data-id="element-3000">
                                Price
                              </th>
                            </tr>
                          </thead>
                          <tbody data-id="element-3001">
                            {product.pricingTiers.map((tier: any, index: number) => <tr key={index} className={index === product.currentTier ? 'bg-blue-50' : ''} data-id="element-3002">
                                  <td className="py-1 text-gray-800" data-id="element-3003">
                                    {index === 0 ? `First ${tier.threshold}` : `${product.pricingTiers[index - 1].threshold} - ${tier.threshold}`}
                                  </td>
                                  <td className="py-1 text-right font-medium text-gray-800" data-id="element-3004">
                                    ${tier.price}
                                    {index === product.currentTier && <span className="ml-2 text-xs text-blue-600" data-id="element-3005">
                                        Current
                                      </span>}
                                  </td>
                                </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </> : <div className="bg-amber-50 border border-amber-100 p-4 rounded-lg text-amber-800 flex items-center" data-id="element-3006">
                    <AlertCircleIcon className="h-5 w-5 mr-2" data-id="element-3007" />
                    No available departures at this time. Please check back
                    later or contact us for custom dates.
                  </div>}
              </div> :
          // Simplified availability display for presentation mode
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-6" data-id="element-3008">
                <h3 className="text-xl font-medium text-gray-800 mb-3 flex items-center" data-id="element-3009">
                  <CalendarIcon className="h-6 w-6 text-blue-500 mr-2" data-id="element-3010" />
                  Available Departures
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3011">
                  {product.departures.filter((dep: any) => dep.availability !== 'soldout').slice(0, 4).map((departure: any, index: number) => <div key={index} className="bg-white p-4 rounded-lg border border-blue-100 flex items-center" data-id="element-3012">
                        <div className="p-2 rounded-full bg-blue-100 mr-3" data-id="element-3013">
                          <CalendarIcon className="h-6 w-6 text-blue-600" data-id="element-3014" />
                        </div>
                        <div data-id="element-3015">
                          <div className="font-medium text-gray-800" data-id="element-3016">
                            {new Date(departure.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                          </div>
                          <div className="text-sm text-gray-600" data-id="element-3017">
                            {departure.availability === 'limited' ? 'Limited availability' : 'Available'}
                          </div>
                        </div>
                      </div>)}
                </div>
              </div>}
            <div data-id="element-3018">
              <h3 className={`${isPresentationMode ? 'text-xl' : 'text-lg'} font-medium text-gray-800 mb-3`} data-id="element-3019">
                Highlights
              </h3>
              <ul className="space-y-2" data-id="element-3020">
                {product.highlights.map((highlight: string, index: number) => <li key={index} className="flex items-start" data-id="element-3021">
                    <CheckIcon className={`${isPresentationMode ? 'h-6 w-6' : 'h-5 w-5'} text-green-500 mr-2 flex-shrink-0 mt-0.5`} data-id="element-3022" />
                    <span className={`text-gray-700 ${isPresentationMode ? 'text-lg' : ''}`} data-id="element-3023">
                      {highlight}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>;
      case 'itinerary':
        return <div className="space-y-6" data-id="element-3024">
            {product.itinerary.map((day: any, index: number) => <div key={index} className="border-b border-gray-200 pb-6 last:border-0 last:pb-0" data-id="element-3025">
                <div className="flex items-start" data-id="element-3026">
                  <div className={`flex-shrink-0 bg-blue-100 text-blue-800 font-bold rounded-full ${isPresentationMode ? 'w-12 h-12 text-lg' : 'w-10 h-10'} flex items-center justify-center mr-4`} data-id="element-3027">
                    {day.day}
                  </div>
                  <div className="flex-1" data-id="element-3028">
                    <h3 className={`${isPresentationMode ? 'text-xl' : 'text-lg'} font-medium text-gray-800`} data-id="element-3029">
                      {day.title}
                    </h3>
                    <p className={`text-gray-700 mt-2 ${isPresentationMode ? 'text-lg' : ''}`} data-id="element-3030">
                      {day.description}
                    </p>
                    <div className="flex items-center mt-3 space-x-6" data-id="element-3031">
                      {day.meals && day.meals.length > 0 && <div className="flex items-center" data-id="element-3032">
                          <span className={`${isPresentationMode ? 'text-base' : 'text-sm'} font-medium text-gray-600 mr-2`} data-id="element-3033">
                            Meals:
                          </span>
                          <div className="flex space-x-1" data-id="element-3034">
                            {day.meals.includes('breakfast') && <span className={`px-2 py-1 bg-blue-100 text-blue-800 ${isPresentationMode ? 'text-sm' : 'text-xs'} rounded`} data-id="element-3035">
                                B
                              </span>}
                            {day.meals.includes('lunch') && <span className={`px-2 py-1 bg-green-100 text-green-800 ${isPresentationMode ? 'text-sm' : 'text-xs'} rounded`} data-id="element-3036">
                                L
                              </span>}
                            {day.meals.includes('dinner') && <span className={`px-2 py-1 bg-amber-100 text-amber-800 ${isPresentationMode ? 'text-sm' : 'text-xs'} rounded`} data-id="element-3037">
                                D
                              </span>}
                          </div>
                        </div>}
                      {day.accommodation && !isPresentationMode && <div className="flex items-center" data-id="element-3038">
                          <span className="text-sm font-medium text-gray-600 mr-2" data-id="element-3039">
                            Accommodation:
                          </span>
                          <span className="text-sm text-gray-700" data-id="element-3040">
                            {day.accommodation}
                          </span>
                        </div>}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>;
      case 'usefulInfo':
        return <div className="space-y-6" data-id="element-3041">
            {product.usefulInfo.map((info: any, index: number) => <div key={index} className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-3042">
                <h3 className={`${isPresentationMode ? 'text-xl' : 'text-lg'} font-medium text-gray-800 mb-2 flex items-center`} data-id="element-3043">
                  <InfoIcon className={`${isPresentationMode ? 'h-6 w-6' : 'h-5 w-5'} text-blue-500 mr-2`} data-id="element-3044" />
                  {info.title}
                </h3>
                <p className={`text-gray-700 ${isPresentationMode ? 'text-lg' : ''}`} data-id="element-3045">
                  {info.content}
                </p>
              </div>)}
          </div>;
      case 'price':
        return <div className="space-y-6" data-id="element-3046">
            {!isPresentationMode ? <>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3047">
                  <h3 className="text-lg font-medium text-gray-800 p-4 bg-gray-50 border-b border-gray-200" data-id="element-3048">
                    Departure Dates & Prices
                  </h3>
                  <div className="overflow-x-auto" data-id="element-3049">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-3050">
                      <thead className="bg-gray-50" data-id="element-3051">
                        <tr data-id="element-3052">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3053">
                            Departure Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3054">
                            Price (per person)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3055">
                            Availability
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3056">
                            Seats Left
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3057">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-3058">
                        {product.departures.map((departure: any, index: number) => <tr key={index} className={departure.availability === 'soldout' ? 'bg-gray-50' : ''} data-id="element-3059">
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-3060">
                                <div className="flex items-center" data-id="element-3061">
                                  <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3062" />
                                  <span className="text-sm text-gray-900" data-id="element-3063">
                                    {new Date(departure.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-3064">
                                <div className="text-sm font-medium text-blue-600" data-id="element-3065">
                                  ${departure.price}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-3066">
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                                ${departure.availability === 'available' ? 'bg-green-100 text-green-800' : departure.availability === 'limited' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-id="element-3067">
                                  {departure.availability === 'available' ? 'Available' : departure.availability === 'limited' ? 'Limited Seats' : 'Sold Out'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3068">
                                {departure.seats > 0 ? `${departure.seats} seats` : 'None'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3069">
                                {departure.availability !== 'soldout' ? <button className="text-blue-600 hover:text-blue-900" data-id="element-3070">
                                    Select
                                  </button> : <button className="text-gray-400 cursor-not-allowed" data-id="element-3071">
                                    Sold Out
                                  </button>}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3072">
                  <h3 className="text-lg font-medium text-gray-800 p-4 bg-gray-50 border-b border-gray-200" data-id="element-3073">
                    Pricing Matrix
                  </h3>
                  <div className="overflow-x-auto" data-id="element-3074">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-3075">
                      <thead className="bg-gray-50" data-id="element-3076">
                        <tr data-id="element-3077">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3078">
                            Room Type
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3079">
                            Adult (12+)
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3080">
                            Child (2-11)
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3081">
                            Infant (0-1)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-3082">
                        <tr data-id="element-3083">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-3084">
                            Single Room
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900" data-id="element-3085">
                            ${(product.price * 1.5).toFixed(0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900" data-id="element-3086">
                            N/A
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900" data-id="element-3087">
                            N/A
                          </td>
                        </tr>
                        <tr data-id="element-3088">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-3089">
                            Double/Twin Room
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900" data-id="element-3090">
                            ${product.price}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900" data-id="element-3091">
                            ${(product.price * 0.75).toFixed(0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900" data-id="element-3092">
                            $350
                          </td>
                        </tr>
                        <tr data-id="element-3093">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-3094">
                            Triple Room
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900" data-id="element-3095">
                            ${(product.price * 0.9).toFixed(0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900" data-id="element-3096">
                            ${(product.price * 0.7).toFixed(0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900" data-id="element-3097">
                            $350
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </> :
          // Simplified pricing display for presentation mode
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3098">
                <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-3099">
                  <h3 className="text-xl font-medium text-gray-800 mb-4" data-id="element-3100">
                    Available Departures
                  </h3>
                  <div className="space-y-4" data-id="element-3101">
                    {product.departures.filter((dep: any) => dep.availability !== 'soldout').slice(0, 4).map((departure: any, index: number) => <div key={index} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0 last:pb-0" data-id="element-3102">
                          <div className="flex items-center" data-id="element-3103">
                            <CalendarIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-3104" />
                            <span className="text-lg text-gray-800" data-id="element-3105">
                              {new Date(departure.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                            </span>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${departure.availability === 'limited' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-3106">
                            {departure.availability === 'limited' ? 'Limited Seats' : 'Available'}
                          </span>
                        </div>)}
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-3107">
                  <h3 className="text-xl font-medium text-gray-800 mb-4" data-id="element-3108">
                    Accommodation Options
                  </h3>
                  <div className="space-y-4" data-id="element-3109">
                    <div className="p-4 border border-gray-200 rounded-lg" data-id="element-3110">
                      <h4 className="font-medium text-lg text-gray-800" data-id="element-3111">
                        Single Room
                      </h4>
                      <p className="text-gray-600 mt-1" data-id="element-3112">
                        Perfect for solo travelers
                      </p>
                    </div>
                    <div className="p-4 border border-blue-200 rounded-lg bg-blue-50" data-id="element-3113">
                      <h4 className="font-medium text-lg text-gray-800" data-id="element-3114">
                        Double/Twin Room
                      </h4>
                      <p className="text-gray-600 mt-1" data-id="element-3115">
                        Ideal for couples or friends
                      </p>
                      <div className="flex items-center mt-2" data-id="element-3116">
                        <CheckIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-3117" />
                        <span className="text-gray-700" data-id="element-3118">
                          Most popular choice
                        </span>
                      </div>
                    </div>
                    <div className="p-4 border border-gray-200 rounded-lg" data-id="element-3119">
                      <h4 className="font-medium text-lg text-gray-800" data-id="element-3120">
                        Triple Room
                      </h4>
                      <p className="text-gray-600 mt-1" data-id="element-3121">
                        Great for families or groups
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
          </div>;
      case 'documents':
        return <div className="grid grid-cols-2 gap-4" data-id="element-3122">
            {product.documents.map((doc: any, index: number) => <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-id="element-3123">
                <div className="flex items-center" data-id="element-3124">
                  <div className={`p-2 bg-blue-100 rounded-lg mr-3 ${isPresentationMode ? 'p-3' : ''}`} data-id="element-3125">
                    <FileTextIcon className={`${isPresentationMode ? 'h-8 w-8' : 'h-6 w-6'} text-blue-600`} data-id="element-3126" />
                  </div>
                  <div data-id="element-3127">
                    <h3 className={`font-medium text-gray-800 ${isPresentationMode ? 'text-lg' : ''}`} data-id="element-3128">
                      {doc.title}
                    </h3>
                    {!isPresentationMode && <p className="text-xs text-gray-500" data-id="element-3129">
                        {doc.type.toUpperCase()} • {doc.size}
                      </p>}
                  </div>
                </div>
                <div className="flex space-x-2" data-id="element-3130">
                  {!isPresentationMode && <button className="p-1.5 bg-gray-100 rounded text-gray-600 hover:bg-gray-200" data-id="element-3131">
                      <PrinterIcon className="h-4 w-4" data-id="element-3132" />
                    </button>}
                  <button className={`${isPresentationMode ? 'px-3 py-1.5 text-sm' : 'p-1.5'} bg-blue-100 rounded text-blue-600 hover:bg-blue-200 ${isPresentationMode ? 'flex items-center' : ''}`} data-id="element-3133">
                    <DownloadIcon className={`${isPresentationMode ? 'h-5 w-5 mr-1' : 'h-4 w-4'}`} data-id="element-3134" />
                    {isPresentationMode && <span data-id="element-3135">Download</span>}
                  </button>
                </div>
              </div>)}
          </div>;
      case 'videos':
        return <div className="grid grid-cols-2 gap-6" data-id="element-3136">
            {product.videos.map((video: any, index: number) => <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3137">
                <div className="relative" data-id="element-3138">
                  <img src={video.thumbnail} alt={video.title} className={`w-full ${isPresentationMode ? 'h-64' : 'h-48'} object-cover`} data-id="element-3139" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-3140">
                    <button className={`${isPresentationMode ? 'p-4' : 'p-3'} bg-white bg-opacity-80 rounded-full hover:bg-opacity-100 transition-opacity`} data-id="element-3141">
                      <PlayIcon className={`${isPresentationMode ? 'h-10 w-10' : 'h-8 w-8'} text-blue-600`} data-id="element-3142" />
                    </button>
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded" data-id="element-3143">
                    {video.duration}
                  </div>
                </div>
                <div className="p-4" data-id="element-3144">
                  <h3 className={`font-medium text-gray-800 ${isPresentationMode ? 'text-lg' : ''}`} data-id="element-3145">
                    {video.title}
                  </h3>
                </div>
              </div>)}
          </div>;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-3146">
      {/* Back button - hidden in presentation mode */}
      {!isPresentationMode && <button className="flex items-center text-gray-600 hover:text-gray-800" onClick={() => navigate('/products')} data-id="element-3147">
          <ChevronLeftIcon className="h-5 w-5 mr-1" data-id="element-3148" />
          Back to Products
        </button>}

      {/* Product Header */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-3149">
        <div className={`relative ${isPresentationMode ? 'h-96 product-image' : 'h-80'}`} data-id="element-3150">
          <img src={product.image} alt={product.title} className="w-full h-full object-cover" data-id="element-3151" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-3152"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white" data-id="element-3153">
            <div className="flex items-center mb-2" data-id="element-3154">
              <span className={`px-2 py-1 bg-blue-600 text-white ${isPresentationMode ? 'text-sm' : 'text-xs'} font-medium rounded-md mr-2`} data-id="element-3155">
                {product.category === 'git' ? 'Group Tour' : product.category === 'fit' ? 'Custom Tour' : product.category}
              </span>
              <div className="flex items-center" data-id="element-3156">
                <StarIcon className={`${isPresentationMode ? 'h-5 w-5' : 'h-4 w-4'} text-yellow-400 mr-1`} data-id="element-3157" />
                <span className={`${isPresentationMode ? 'text-base' : 'text-sm'} font-medium`} data-id="element-3158">
                  {product.rating}
                </span>
                <span className={`${isPresentationMode ? 'text-base' : 'text-sm'} text-gray-300 ml-1`} data-id="element-3159">
                  ({product.reviews} reviews)
                </span>
              </div>
            </div>
            <h1 className={`${isPresentationMode ? 'text-4xl' : 'text-3xl'} font-bold`} data-id="element-3160">
              {product.title}
            </h1>
            <div className="flex items-center mt-2" data-id="element-3161">
              <MapPinIcon className={`${isPresentationMode ? 'h-6 w-6' : 'h-5 w-5'} mr-1`} data-id="element-3162" />
              <span className={`${isPresentationMode ? 'text-xl' : 'text-lg'}`} data-id="element-3163">
                {product.destination}
              </span>
            </div>
          </div>
          {!isPresentationMode && <div className="absolute top-4 right-4 flex space-x-2" data-id="element-3164">
              <button className="p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30" data-id="element-3165">
                <HeartIcon className="h-5 w-5" data-id="element-3166" />
              </button>
              <button className="p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30" data-id="element-3167">
                <ShareIcon className="h-5 w-5" data-id="element-3168" />
              </button>
            </div>}
        </div>

        {/* Quick Info Bar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-3169">
          <div className="flex items-center space-x-6" data-id="element-3170">
            <div className="flex items-center" data-id="element-3171">
              <ClockIcon className={`${isPresentationMode ? 'h-6 w-6' : 'h-5 w-5'} text-gray-400 mr-2`} data-id="element-3172" />
              <div data-id="element-3173">
                <p className={`${isPresentationMode ? 'text-base' : 'text-sm'} font-medium text-gray-800`} data-id="element-3174">
                  Duration
                </p>
                <p className={`${isPresentationMode ? 'text-base' : 'text-sm'} text-gray-600`} data-id="element-3175">
                  {product.duration}
                </p>
              </div>
            </div>
            <div className="flex items-center" data-id="element-3176">
              <UsersIcon className={`${isPresentationMode ? 'h-6 w-6' : 'h-5 w-5'} text-gray-400 mr-2`} data-id="element-3177" />
              <div data-id="element-3178">
                <p className={`${isPresentationMode ? 'text-base' : 'text-sm'} font-medium text-gray-800`} data-id="element-3179">
                  Group Size
                </p>
                <p className={`${isPresentationMode ? 'text-base' : 'text-sm'} text-gray-600`} data-id="element-3180">
                  {product.groupSize}
                </p>
              </div>
            </div>
            <div className="flex items-center" data-id="element-3181">
              <CalendarIcon className={`${isPresentationMode ? 'h-6 w-6' : 'h-5 w-5'} text-gray-400 mr-2`} data-id="element-3182" />
              <div data-id="element-3183">
                <p className={`${isPresentationMode ? 'text-base' : 'text-sm'} font-medium text-gray-800`} data-id="element-3184">
                  Departures
                </p>
                <p className={`${isPresentationMode ? 'text-base' : 'text-sm'} text-gray-600`} data-id="element-3185">
                  {product.departures.filter((d: any) => d.availability !== 'soldout').length}{' '}
                  dates available
                </p>
              </div>
            </div>
          </div>
          <div className="text-right" data-id="element-3186">
            {isPresentationMode ? <div className="inline-block bg-blue-100 px-4 py-2 rounded-lg" data-id="element-3187">
                <p className="text-xl font-bold text-blue-600" data-id="element-3188">
                  From ${product.price}
                </p>
                <p className="text-sm text-blue-600" data-id="element-3189">per person</p>
              </div> : <>
                <p className="text-sm text-gray-500" data-id="element-3190">Starting from</p>
                <p className="text-2xl font-bold text-blue-600" data-id="element-3191">
                  ${product.price}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3192">per person</p>
              </>}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6" data-id="element-3193">
          <div className="flex overflow-x-auto" data-id="element-3194">
            {[{
            id: 'overview',
            label: 'Overview'
          }, {
            id: 'itinerary',
            label: 'Itinerary'
          }, {
            id: 'usefulInfo',
            label: 'Useful Info'
          }, {
            id: 'price',
            label: isPresentationMode ? 'Dates & Options' : 'Price'
          }, {
            id: 'documents',
            label: 'Documents'
          }, {
            id: 'videos',
            label: 'Videos'
          }].map(tab => <button key={tab.id} className={`px-4 py-3 ${isPresentationMode ? 'text-base' : 'text-sm'} font-medium whitespace-nowrap ${activeTab === tab.id ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'}`} onClick={() => setActiveTab(tab.id)} data-id="element-3195">
                {tab.label}
              </button>)}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6" data-id="element-3196">{renderTabContent()}</div>
      </div>

      {/* Actions Bar - only shown in agent mode */}
      {!isPresentationMode && <div className="bg-white rounded-lg shadow-md p-4 agent-only" data-id="element-3197">
          <div className="flex justify-between items-center" data-id="element-3198">
            <div className="flex items-center space-x-4" data-id="element-3199">
              <button className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200" data-id="element-3200">
                <PrinterIcon className="h-4 w-4 mr-2" data-id="element-3201" />
                Print
              </button>
              <button className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200" data-id="element-3202">
                <MailIcon className="h-4 w-4 mr-2" data-id="element-3203" />
                Email
              </button>
              <button className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200" data-id="element-3204">
                <PhoneIcon className="h-4 w-4 mr-2" data-id="element-3205" />
                Call Client
              </button>
            </div>
            <div className="flex items-center space-x-3" data-id="element-3206">
              <button className="px-4 py-2 border border-blue-600 text-blue-600 font-medium rounded-md hover:bg-blue-50" onClick={handleOpenQuoteFlow} data-id="element-3207">
                Create Quote
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700" data-id="element-3208">
                Book Now
              </button>
            </div>
          </div>
        </div>}

      {/* Quote Flow Modal */}
      {product && <QuoteFlow product={product} isOpen={showQuoteFlow} onClose={() => setShowQuoteFlow(false)} data-id="element-3209" />}

      {/* Customer-friendly CTA in presentation mode */}
      {isPresentationMode && <div className="bg-blue-600 text-white rounded-lg shadow-md p-6 text-center" data-id="element-3210">
          <h3 className="text-2xl font-bold mb-2" data-id="element-3211">
            Ready to Experience This Amazing Journey?
          </h3>
          <p className="text-blue-100 mb-4" data-id="element-3212">
            Ask your travel consultant about available dates and booking options
          </p>
          <button className="px-6 py-3 bg-white text-blue-600 font-bold rounded-md hover:bg-blue-50 inline-flex items-center" data-id="element-3213">
            <CalendarIcon className="h-5 w-5 mr-2" data-id="element-3214" />
            Check Availability
          </button>
        </div>}
    </div>;
};